import React, { useState, useContext, useCallback, useEffect } from "react"

import { RootContext } from 'RootContext';

// core components
import { Accordion, AccordionSummary } from '@material-ui/core'
import AccordionDetails from '@material-ui/core/AccordionActions'
import Box from "@material-ui/core/Box"
import Card from "components/Card/Card.js"
import CardBody from "components/Card/CardBody.js"
import CardHeader from "components/Card/CardHeader.js"
import CloseIcon from "@material-ui/icons/Close"
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"
import IconButton from "@material-ui/core/IconButton"
import Typography from "@material-ui/core/Typography"
import Toolbar from '@material-ui/core/Toolbar';
import useScrollTrigger from "@material-ui/core/useScrollTrigger"
import { titleCase } from "title-case"

// CSS
import { useJobsStyles } from "styles/jobs/jobsCss"

//Pegaso component
import { fetchWithToken } from "components/Fetch/api-fetch"
import ErrorUtils from "components/Tools/ErrorUtils"
import FrameListToolbar from "views/FramesManagement/FrameListToolbar"
import FrameListTable from "views/FramesManagement/FrameListTable"

export default function JobFrame(props) {
  const { companyId, apiUrl, access_token, refresh_token, setAccess_token, t } = useContext(RootContext)
  const { handleFrameUpload, setOpenFrameList, onHandleClose, frameBrands, frameSuppliers } = props
  const classes = useJobsStyles()
  const [count, setCount] = useState(0)
  const [frameSupplierId, setFrameSupplierId] = useState("")
  const [frameBrandId, setFrameBrandId] = useState("")
  const [searchModelName, setSearchModelName] = useState("")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [frames, setFrames] = useState([])
  const [isFetchingFrames, setIsFetchingFrames] = useState(false)
  const [frameBrandsToShow, setFrameBrandsToShow] = useState(frameBrands)
  const [orderBy, setOrderBy] = useState("")
  const [order, setOrder] = useState("desc")

  function ElevationScroll(props) {
    const { children, window } = props
    const trigger = useScrollTrigger({
      disableHysteresis: true,
      threshold: 0,
      target: window ? window() : undefined,
    })

    return React.cloneElement(children, {
      elevation: trigger ? 4 : 0,
    })
  }

  /**********************************************
   *  FETCH
   **********************************************/

  // Fetch dei frame per supplier
  const fetchFramesBySupplier = useCallback((frameSupplierId) => {
    const url = `${apiUrl}/companies/${companyId}/listFrames/frameSupplier/${frameSupplierId}`
    fetchWithToken(url, {
      method: "GET",
      headers: {
        Accept: +"application/json",
        "Content-Type": "application/json"
      },
      apiUrl: apiUrl,
      companyId: companyId,
      access_token: access_token,
      refresh_token: refresh_token,
      setAccess_token: setAccess_token
    })
      .then(response => {
        const body = response.body
        const { frames } = body
        setFrames(frames)
        setCount(frames.length)
        setIsFetchingFrames(false)
      })
      .catch(err => {
        setIsFetchingFrames(false)
        ErrorUtils.errorLog(`${url} => fetchFramesBySupplier Error`, err.message)
      })
  }, [access_token, apiUrl, companyId, refresh_token, setAccess_token])

  // Fetch dei frame per brand
  const fetchFramesByBrand = useCallback((frameBrandId) => {
    const url = `${apiUrl}/companies/${companyId}/listFrames/frameBrand/${frameBrandId}`
    fetchWithToken(url, {
      method: "GET",
      headers: {
        Accept: +"application/json",
        "Content-Type": "application/json"
      },
      apiUrl: apiUrl,
      companyId: companyId,
      access_token: access_token,
      refresh_token: refresh_token,
      setAccess_token: setAccess_token
    })
      .then(response => {
        const body = response.body
        const { frames } = body
        setFrames(frames)
        setCount(frames.length)
        setIsFetchingFrames(false)
      })
      .catch(err => {
        setIsFetchingFrames(false)
        ErrorUtils.errorLog(`${url} => fetchFramesByBrand Error`, err.message)
      })
  }, [access_token, apiUrl, companyId, refresh_token, setAccess_token])

  // Fetch dei frame per supplier e brand
  const fetchFramesBySupplierAndBrand = useCallback((frameSupplierId, frameBrandId) => {
    const url = `${apiUrl}/companies/${companyId}/listFrames/frameSupplier/${frameSupplierId}/frameBrand/${frameBrandId}`
    fetchWithToken(url, {
      method: "GET",
      headers: {
        Accept: + "application/json",
        "Content-Type": "application/json"
      },
      apiUrl: apiUrl,
      companyId: companyId,
      access_token: access_token,
      refresh_token: refresh_token,
      setAccess_token: setAccess_token
    })
      .then(response => {
        const body = response.body
        const { frames } = body
        setFrames(frames)
        setCount(frames.length)
        setIsFetchingFrames(false)
      })
      .catch(err => {
        setIsFetchingFrames(false)
        ErrorUtils.errorLog(`${url} => fetchFramesBySupplierAndBrand Error`, err.message)
      })
  }, [access_token, apiUrl, companyId, refresh_token, setAccess_token])

  // Fetch dei frame per model id
  const fetchFramesByModelId = useCallback((modelName) => {
    const url = `${apiUrl}/companies/${companyId}/listFrames/modelName/${modelName}`
    fetchWithToken(url, {
      method: "GET",
      headers: {
        Accept: +"application/json",
        "Content-Type": "application/json"
      },
      apiUrl: apiUrl,
      companyId: companyId,
      access_token: access_token,
      refresh_token: refresh_token,
      setAccess_token: setAccess_token
    })
      .then(response => {
        const body = response.body
        const { frames } = body
        setFrames(frames)
        setCount(frames.length)
        setIsFetchingFrames(false)
      })
      .catch(err => {
        setIsFetchingFrames(false)
        ErrorUtils.errorLog(`${url} => fetchFramesByModelId Error`, err.message)
      })

  }, [access_token, apiUrl, companyId, refresh_token, setAccess_token])

  /**********************************************
     *  HANDLER
     **********************************************/

  // Click sort by coloumn
  function handleRequestSort(event, property) {
    const isDesc = orderBy === property && order === "desc"
    setOrder(isDesc ? "asc" : "desc")
    setOrderBy(property)
  }

  //Handler cambio filtri
  function handleChangeFilter(filterField, targetValue) {
    setFrames([])
    setCount(0)
    setPage(0)
    if (filterField === "frameSupplier") {
      // Rifiltra i brands di quel supplier
      var newFrameBrands = frameBrands
      if (targetValue !== "" && targetValue !== "0") {
        newFrameBrands = frameBrands.filter(option =>
          option.supplierId === parseInt(targetValue)
        )
        newFrameBrands.unshift({
          value: 0,
          text: ""
        })
      }
      setFrameBrandsToShow(newFrameBrands)
      setFrameSupplierId(targetValue)
    }
    else if (filterField === "frameBrand") {
      setFrameBrandId(targetValue)
    }
    else if (filterField === "searchModelName") {
      setSearchModelName(targetValue)
    }
  }

  useEffect(() => {
    if (searchModelName !== "") {
      setIsFetchingFrames(true)
      fetchFramesByModelId(searchModelName)
    } else if (frameSupplierId !== "" && frameSupplierId !== "0" && frameBrandId !== "" && frameBrandId !== "0") {
      setIsFetchingFrames(true)
      fetchFramesBySupplierAndBrand(frameSupplierId, frameBrandId)
    } else if (frameBrandId !== "" && frameBrandId !== "0") {
      setIsFetchingFrames(true)
      fetchFramesByBrand(frameBrandId)
    } else if (frameSupplierId !== "" && frameSupplierId !== "0") {
      setIsFetchingFrames(true)
      fetchFramesBySupplier(frameSupplierId)
    }
  }, [frameBrandId, frameSupplierId,
    fetchFramesByBrand, fetchFramesBySupplier, fetchFramesBySupplierAndBrand, fetchFramesByModelId,
    searchModelName, setIsFetchingFrames])

  return (
    <div>
      {/* X CHIUSURA IN ALTO */}
      <ElevationScroll {...props}>
        <Box display="flex" flexDirection="row-reverse" p={1}>
          <Box p={1}>
            <IconButton
              aria-label={t("10238")}
              variant="outlined"
              onClick={onHandleClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </ElevationScroll>

      {/*Pannello filtri*/}
      {<Accordion defaultExpanded={true}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography className={classes.heading}>{titleCase(t("10087"))}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Card>

            <CardHeader color="primary">
              <FrameListToolbar
                frameBrandId={frameBrandId}
                frameSupplierId={frameSupplierId}
                handleChangeFilter={(filterField, targetValue) => {
                  handleChangeFilter(filterField, targetValue)
                }}
                selectOptionFrameSuppliers={frameSuppliers}
                selectOptionFrameBrands={frameBrandsToShow}
              />
            </CardHeader>

          </Card>
        </AccordionDetails>
      </Accordion>}
      <Card>
        <CardBody>
          <Toolbar id="back-to-top-anchor" />
          <FrameListTable
            countFramesRows={count}
            handleFrameUpload={handleFrameUpload}
            isFullTable={false}
            loading={isFetchingFrames}
            onRequestSort={handleRequestSort}
            order={order}
            orderBy={orderBy}
            page={page}
            rows={frames}
            rowsPerPage={rowsPerPage}
            setOpenFrameList={setOpenFrameList}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        </CardBody>
      </Card>
    </div >
  )
}